import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl } from '@angular/forms';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';

import { HelpSection } from '../../models/help-section.model';
import { HtmlEditorComponent } from '../html-editor/html-editor.component';
import { sanitizeHelpHtmlFragment } from '../../utils/help-html-sanitize.util';

/**
 * Visor de ayuda con menú lateral de secciones (documento v1).
 */
@Component({
  selector: 'app-help-section-viewer',
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavModule,
    MatListModule,
    TranslateModule,
    HtmlEditorComponent,
  ],
  templateUrl: './help-section-viewer.component.html',
  styleUrl: './help-section-viewer.component.scss',
})
export class HelpSectionViewerComponent implements OnChanges {

  private sanitizer = inject(DomSanitizer);

  @Input() sections: HelpSection[] = [];
  @Input() readOnly = true;
  @Input() formControlEditing: FormControl<string | null> = new FormControl();

  /** Emite el documento serializado al guardar edición inline */
  @Output() sectionsChange = new EventEmitter<HelpSection[]>();

  public activeSectionId: string | null = null;
  public activeSection: HelpSection | null = null;
  public sanitizedContent: SafeHtml = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sections']) {
      this.pickActiveSection();
    }
  }

  get sortedSections(): HelpSection[] {
    return [...this.sections].sort((a, b) => a.order - b.order);
  }

  selectSection(section: HelpSection): void {
    if (!this.readOnly) {
      this.persistEditorToSections();
    }
    this.activeSectionId = section.id;
    this.activeSection = section;
    this.updateSanitizedContent(section.content);
    if (!this.readOnly) {
      this.formControlEditing.setValue(section.content);
    }
  }

  /** Llamar antes de guardar en el padre */
  persistEditorToSections(): void {
    if (!this.activeSectionId) return;

    const content = this.formControlEditing.value ?? '';
    this.sections = this.sections.map((s) =>
      s.id === this.activeSectionId ? { ...s, content } : s,
    );
    this.sectionsChange.emit([...this.sections]);
  }

  private pickActiveSection(): void {
    if (!this.sections.length) {
      this.activeSection = null;
      return;
    }
    const exists = this.activeSectionId && this.sections.some((s) => s.id === this.activeSectionId);
    const section = exists
      ? this.sections.find((s) => s.id === this.activeSectionId)!
      : this.sections[0];
    this.activeSectionId = section.id;
    this.activeSection = section;
    this.updateSanitizedContent(section.content);
    if (!this.readOnly) {
      this.formControlEditing.setValue(section.content);
    }
  }

  private updateSanitizedContent(html: string): void {
    const clean = sanitizeHelpHtmlFragment(html ?? '');
    this.sanitizedContent = this.sanitizer.bypassSecurityTrustHtml(clean);
  }
}
